import React from 'react';

/**
 * Layered top-to-bottom relationship tree, matching the legacy dagre-d3 modal
 * (rect nodes, arrowed edges with grey "supersedes"/"cancels" chips, current
 * document highlighted #f1b859). Dependency-free SVG with wheel zoom and drag
 * pan — the legacy stack (dagre-d3-react + d3v3) predates React 18, so the
 * layered layout is computed here instead.
 */

const NODE_H = 34;
const RANK_GAP = 110;
const X_GAP = 28;

function nodeWidth(label) {
  return Math.max(90, (label || '').length * 8 + 28);
}

/** rank = longest path from any root, following edge direction. */
function layout(nodes, edges) {
  const rank = {};
  nodes.forEach((n) => (rank[n.id] = 0));
  // relax edges |V| times (graphs here are tiny chains; cycle-safe)
  for (let i = 0; i < nodes.length; i += 1) {
    let changed = false;
    edges.forEach((e) => {
      if (rank[e.source] !== undefined && rank[e.target] !== undefined) {
        const want = rank[e.source] + 1;
        if (rank[e.target] < want && want <= nodes.length) {
          rank[e.target] = want;
          changed = true;
        }
      }
    });
    if (!changed) break;
  }
  const byRank = {};
  nodes.forEach((n) => {
    (byRank[rank[n.id]] = byRank[rank[n.id]] || []).push(n);
  });
  const placed = {};
  Object.keys(byRank)
    .sort((a, b) => a - b)
    .forEach((r) => {
      const row = byRank[r];
      const totalW =
        row.reduce((acc, n) => acc + nodeWidth(n.label), 0) +
        X_GAP * (row.length - 1);
      let x = -totalW / 2;
      row.forEach((n) => {
        const w = nodeWidth(n.label);
        placed[n.id] = { ...n, x: x + w / 2, y: r * RANK_GAP, w, h: NODE_H };
        x += w + X_GAP;
      });
    });
  return placed;
}

const TreeGraph = ({ nodes, edges, onNodeClick, height = 500 }) => {
  const [view, setView] = React.useState({ k: 1, tx: 0, ty: 0 });
  const dragging = React.useRef(null);
  const svgRef = React.useRef(null);

  const placed = React.useMemo(() => layout(nodes, edges), [nodes, edges]);
  const pts = Object.values(placed);
  if (pts.length === 0) return null;

  const minX = Math.min(...pts.map((p) => p.x - p.w / 2)) - 30;
  const maxX = Math.max(...pts.map((p) => p.x + p.w / 2)) + 30;
  const minY = Math.min(...pts.map((p) => p.y - NODE_H)) - 20;
  const maxY = Math.max(...pts.map((p) => p.y + NODE_H)) + 20;

  const onWheel = (e) => {
    e.preventDefault();
    setView((v) => ({
      ...v,
      k: Math.min(4, Math.max(0.25, v.k * (e.deltaY < 0 ? 1.12 : 0.9))),
    }));
  };
  const onPointerDown = (e) => {
    dragging.current = { x: e.clientX, y: e.clientY };
  };
  const onPointerMove = (e) => {
    if (!dragging.current) return;
    const dx = e.clientX - dragging.current.x;
    const dy = e.clientY - dragging.current.y;
    dragging.current = { x: e.clientX, y: e.clientY };
    setView((v) => ({ ...v, tx: v.tx + dx, ty: v.ty + dy }));
  };
  const stopDrag = () => {
    dragging.current = null;
  };

  return (
    <svg
      ref={svgRef}
      className="pp-tree-graph"
      viewBox={`${minX} ${minY} ${maxX - minX} ${maxY - minY}`}
      style={{ width: '100%', height, cursor: 'all-scroll' }}
      onWheel={onWheel}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={stopDrag}
      onPointerLeave={stopDrag}
      role="img"
      aria-label="Document relationship graph"
    >
      <defs>
        <marker
          id="pp-arrow"
          viewBox="0 0 10 10"
          refX="9"
          refY="5"
          markerWidth="7"
          markerHeight="7"
          orient="auto-start-reverse"
        >
          <path d="M 0 0 L 10 5 L 0 10 z" fill="#111" />
        </marker>
      </defs>
      <g transform={`translate(${view.tx},${view.ty}) scale(${view.k})`}>
        {edges.map((e, i) => {
          const s = placed[e.source];
          const t = placed[e.target];
          if (!s || !t) return null;
          const x1 = s.x;
          const y1 = s.y + NODE_H / 2;
          const x2 = t.x;
          const y2 = t.y - NODE_H / 2 - 2;
          const mx = (x1 + x2) / 2;
          const my = (y1 + y2) / 2;
          const lw = e.label.length * 7.2 + 10;
          return (
            <g key={`e-${i}`}>
              <line
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="#111"
                strokeWidth="1.5"
                markerEnd="url(#pp-arrow)"
              />
              <rect
                x={mx - lw / 2}
                y={my - 10}
                width={lw}
                height={20}
                fill="#e3e3e3"
              />
              <text
                x={mx}
                y={my + 4}
                textAnchor="middle"
                fontSize="13"
                fontWeight="600"
              >
                {e.label}
              </text>
            </g>
          );
        })}
        {pts.map((p) => (
          <g
            key={p.id}
            onClick={() => onNodeClick && onNodeClick(p)}
            style={{ cursor: 'pointer' }}
          >
            <rect
              x={p.x - p.w / 2}
              y={p.y - NODE_H / 2}
              width={p.w}
              height={NODE_H}
              rx="5"
              ry="5"
              fill={p.mark ? '#f1b859' : '#c9c9c9'}
              stroke="#333"
            />
            <title>{p.label}</title>
            <text
              x={p.x}
              y={p.y + 4.5}
              textAnchor="middle"
              fontSize="13"
              fontWeight="700"
            >
              {p.label}
            </text>
          </g>
        ))}
      </g>
    </svg>
  );
};

export default TreeGraph;
